import { useEffect, useRef } from "react";
import { usePreferences } from "./usePreferences";
import { normalizePersonality, usePersonalityStore } from "@/store/personalityStore";

/**
 * Keeps the Personality setting (Pro / Fun) in step with the user's
 * preferences on the server, the same way useThemeSync does for the theme:
 *
 *   - first preferences load: the server's value wins (the cross-device
 *     promise), unless the person already chose in this session;
 *   - after that, every change here is written back.
 *
 * A server that does not know the field yet (an API older than the
 * `personality` preference) answers without it; the local choice then stands
 * and is still sent, so it lands as soon as the API does.
 *
 * Mount once at the app root — see Theme.tsx.
 */
export function usePersonalitySync() {
  const personality = usePersonalityStore((s) => s.personality);
  const { preferences, updatePreferences } = usePreferences();
  const hydratedRef = useRef(false);

  // Server → store, once.
  useEffect(() => {
    if (!preferences || hydratedRef.current) return;
    hydratedRef.current = true;
    const server = normalizePersonality(preferences.personality);
    const { chosenLocally, adoptServerPersonality } = usePersonalityStore.getState();
    const local = usePersonalityStore.getState().personality;
    if (server === local) return;
    if (server && !chosenLocally) {
      adoptServerPersonality(server);
    } else if (chosenLocally) {
      // Chosen before preferences arrived: the store → server effect below
      // was still waiting for hydration, so send it now.
      updatePreferences({ personality: local });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferences]);

  // Store → server, after hydration.
  useEffect(() => {
    if (!hydratedRef.current || !preferences) return;
    if (normalizePersonality(preferences.personality) === personality) return;
    updatePreferences({ personality });
    // Not on preferences: that is server state, and depending on it would
    // race the write just fired.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personality]);
}
